import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { companyInformationQueryKey } from "@/hooks/queries/useGetCompanyInformation";
import { dashboardDataQueryKey } from "@/hooks/queries/useGetDashboardData";
import { portfolioMatrixQueryKey } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { ALL_USERS_KEY } from "@/lib/constants";
import { guacHttpClient } from "@/lib/http";
import {
  CompanyFile,
  CompanyInformation,
  CreateCompanyFileRequest,
  CreateOrUpdateCompanyInformationRequest,
  GetCompanyInformationResponse,
  HttpResponse,
} from "@/types";

export const useUpdateSetCompanyLogo = (isSmallLogo: boolean) => {
  const companyId = useCurrentCompanyId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      companyName,
    }: {
      file: File | null;
      companyName: string;
    }) => {
      if (file) {
        const response = await guacHttpClient.postForm<
          CompanyFile,
          HttpResponse<CompanyFile>,
          CreateCompanyFileRequest
        >(`companies/${companyId}/files`, {
          companyFile: file,
          name: companyName,
          isLogo: !isSmallLogo,
          isSmallLogo,
        });
        queryClient.setQueryData<CompanyFile[]>(
          ["files", companyId],
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (cached) => [response.data, ...(cached || [])],
        );
      } else {
        const response = await guacHttpClient.get<
          GetCompanyInformationResponse,
          HttpResponse<GetCompanyInformationResponse>
        >(`companies/${companyId}/company-information`);
        const information = response.data.companyInformation;
        if (!information) throw new Error("Company information is unavailable");
        await guacHttpClient.patch<
          CompanyInformation,
          HttpResponse<CompanyInformation>,
          CreateOrUpdateCompanyInformationRequest
        >(`companies/${companyId}/company-informations/${information.id}`, {
          companyInformation: {
            ...information.companyInformation,
            [isSmallLogo ? "smallLogoCompanyFileId" : "logoCompanyFileId"]:
              null,
          },
        });
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: companyInformationQueryKey(companyId),
        }),
        queryClient.invalidateQueries({ queryKey: ["files", companyId] }),
        queryClient.invalidateQueries({
          queryKey: dashboardDataQueryKey(ALL_USERS_KEY).slice(0, 1),
        }),
        queryClient.invalidateQueries({ queryKey: portfolioMatrixQueryKey }),
      ]);
    },
  });
};
